/**
 * Pure functions that turn selected dictionaries + hand-typed hints into the
 * flat `enhancementHints` array the batch upload API accepts. Zero React/DOM
 * dependency — see `docs/dictionary-batch-transcription-plan.md` §5.2. Shared
 * by the web upload and the desktop end-of-recording window.
 */
import {
  AUDIO_BATCH_ENHANCEMENT_HINT_SAFE_REGEX,
  AUDIO_BATCH_MAX_ENHANCEMENT_HINT_CHARS,
} from './audio-upload.js'

/** What expansion reads from a dictionary entry. */
export interface HintSourceEntry {
  term: string
}

/**
 * Trim + validate a hint word; null when empty, too long (>30 chars — the
 * enhancement hint cap, stricter than a dictionary entry's 80-char limit), or
 * carrying control chars / line separators. The single normalize rule for the
 * upload modal's hand-typed hints AND dictionary-term expansion.
 */
export function normalizeHint(value: string): string | null {
  const trimmed = value.trim()
  if (trimmed.length === 0 || trimmed.length > AUDIO_BATCH_MAX_ENHANCEMENT_HINT_CHARS) return null
  if (!AUDIO_BATCH_ENHANCEMENT_HINT_SAFE_REGEX.test(trimmed)) return null
  return trimmed
}

/** One selected dictionary's raw entries, in the order the caller wants them
 *  expanded. Array position in the `expandHints` input list IS the selection
 *  order — the caller (upload modal) owns that ordering. */
export interface DictionarySource {
  dictionaryId: number
  dictionaryName: string
  entries: readonly HintSourceEntry[]
}

/** One selected dictionary's expanded, deduped, per-source view. */
export interface ExpandedDictionaryGroup {
  dictionaryId: number
  dictionaryName: string
  /** Normalized, non-excluded terms from this dictionary that are visible as
   *  chips. A term is credited to the first source that claims it; dictionary
   *  groups are processed before manual hints. */
  hints: string[]
  /** Count of this dictionary's entries that failed `normalizeHint` (over 30
   *  chars or containing a disallowed character) — never silently dropped. */
  skippedCount: number
  /** Normalized terms claimed by this dictionary but globally excluded from
   *  this upload's enhancement hints. These tombstones preserve ownership so
   *  callers can restore the term to its original source. */
  excluded: string[]
}

/** The hand-typed hint group's expanded, deduped view. Always the last source
 *  in dedupe precedence, per spec §5.2 ("手打區恆在最後"). */
export interface ManualHintGroup {
  hints: string[]
  skippedCount: number
  /** Normalized manual terms globally excluded from this upload. */
  excluded: string[]
}

export interface ExpandedHints {
  dictionaryGroups: ExpandedDictionaryGroup[]
  manual: ManualHintGroup
  /** Total effective (deduped, normalized) hint count across every group —
   *  the number the "已選 N / 50" counter renders. */
  totalCount: number
}

/**
 * Expand selected dictionaries + hand-typed hints into per-source groups,
 * applying `normalizeHint` to every term and deduping across ALL sources —
 * dictionaries in `dictionarySources` order, then manual hints — so a term
 * is only ever represented once (as a visible hint or excluded tombstone),
 * credited to the first source that produced it. Invalid entries are counted
 * (`skippedCount`), never silently dropped. `excludedTerms` are already
 * normalized by caller contract and are compared directly.
 */
export function expandHints(
  dictionarySources: DictionarySource[],
  manualHints: string[],
  excludedTerms: readonly string[] = []
): ExpandedHints {
  const seen = new Set<string>()
  const excluded = new Set(excludedTerms)
  const dictionaryGroups: ExpandedDictionaryGroup[] = []

  for (const source of dictionarySources) {
    const hints: string[] = []
    const excludedHints: string[] = []
    let skippedCount = 0
    for (const entry of source.entries) {
      const normalized = normalizeHint(entry.term)
      if (normalized === null) {
        skippedCount += 1
        continue
      }
      if (seen.has(normalized)) continue
      seen.add(normalized)
      if (excluded.has(normalized)) {
        excludedHints.push(normalized)
      } else {
        hints.push(normalized)
      }
    }
    dictionaryGroups.push({
      dictionaryId: source.dictionaryId,
      dictionaryName: source.dictionaryName,
      hints,
      skippedCount,
      excluded: excludedHints,
    })
  }

  const manualHintsOut: string[] = []
  const manualExcludedHints: string[] = []
  let manualSkippedCount = 0
  for (const raw of manualHints) {
    const normalized = normalizeHint(raw)
    if (normalized === null) {
      manualSkippedCount += 1
      continue
    }
    if (seen.has(normalized)) continue
    seen.add(normalized)
    if (excluded.has(normalized)) {
      manualExcludedHints.push(normalized)
    } else {
      manualHintsOut.push(normalized)
    }
  }

  const totalCount =
    dictionaryGroups.reduce((sum, group) => sum + group.hints.length, 0) + manualHintsOut.length

  return {
    dictionaryGroups,
    manual: {
      hints: manualHintsOut,
      skippedCount: manualSkippedCount,
      excluded: manualExcludedHints,
    },
    totalCount,
  }
}

/**
 * Flatten expanded hints into the ordered array the submit payload sends as
 * `enhancementHints`: each dictionary group's hints (in group order, then
 * entry order within the group), followed by manual hints.
 */
export function flattenEnhancementHints(expanded: ExpandedHints): string[] {
  return [...expanded.dictionaryGroups.flatMap((group) => group.hints), ...expanded.manual.hints]
}
